import { useState, type ChangeEvent } from 'react';
import { uploadBinary, computeSha256Hex, type UploadResult } from '../services/binaryUpload';
import type { Channel } from '../services/releases';

// Sprint Auto-update F6 -- Component reutilizavel pra upload binary R2.
//
// Uso A (re-upload em card existente): mostra inline expand com file picker
// + botao Upload. Caller faz PATCH client_build apos onComplete.
//
// Uso B (Create form integrado): inline no form Create. Caller faz POST
// client_build com r2_object_key + text_hash_sha256 vindos do onComplete.
//
// Props:
//   name + version + channel: contexto pro presigned URL endpoint.
//   onComplete: callback executado com {r2_object_key, text_hash_sha256, ...}
//   onCancel: opcional, pra fechar sem upload.
//   compact: bool, true = layout compacto (cards), false = padding maior (modais).

export interface BinaryUploaderProps {
  name: string;
  version: string;
  channel?: Channel;
  onComplete: (result: UploadResult) => void;
  onCancel?: () => void;
  compact?: boolean;
  disabled?: boolean;
}

export function BinaryUploader({
  name, version, channel, onComplete, onCancel, compact, disabled,
}: BinaryUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [hashing, setHashing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [precomputedHash, setPrecomputedHash] = useState<string | null>(null);

  const inputCls =
    'block w-full text-xs text-gray-300 file:mr-3 file:py-1.5 file:px-3 ' +
    'file:rounded file:border-0 file:bg-[#2a2f36] file:text-gray-200 ' +
    'file:cursor-pointer file:hover:bg-[#363b43]';
  const btnCls =
    'bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs ' +
    'font-medium rounded px-3 py-1.5';

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      setPrecomputedHash(null);
      return;
    }
    setFile(f);
    setError(null);
    setProgress(null);
    setPrecomputedHash(null);
    // Computa SHA-256 imediatamente apos selecionar (UX feedback)
    setHashing(true);
    try {
      const sha = await computeSha256Hex(f);
      setPrecomputedHash(sha);
    } catch (err) {
      setError((err as Error).message || 'erro computando SHA-256');
    } finally {
      setHashing(false);
    }
  }

  async function handleUpload(): Promise<void> {
    if (!file || !name.trim() || !version.trim()) {
      setError('file + name + version requeridos');
      return;
    }
    setUploading(true);
    setError(null);
    setProgress({ loaded: 0, total: file.size });
    try {
      const result = await uploadBinary({
        name: name.trim(),
        version: version.trim(),
        channel,
        file,
        onProgress: (loaded, total) => setProgress({ loaded, total }),
      });
      onComplete(result);
    } catch (err) {
      setError((err as Error).message || 'erro no upload');
    } finally {
      setUploading(false);
    }
  }

  const pct = progress
    ? Math.round((progress.loaded / progress.total) * 100)
    : 0;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <div>
        <label htmlFor="binary-uploader-input" className="block text-xs text-gray-500 mb-1">
          Selecionar binary .exe (SHA-256 calculado client-side)
        </label>
        <input
          id="binary-uploader-input"
          type="file"
          accept=".exe,application/octet-stream"
          onChange={(e) => void handleFileChange(e)}
          disabled={disabled || uploading}
          className={inputCls}
        />
      </div>

      {file && (
        <div className="text-xs text-gray-500 font-mono">
          <div>file: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</div>
          {hashing && <div className="text-amber-400">calculando SHA-256...</div>}
          {precomputedHash && (
            <div className="text-emerald-400 break-all">
              sha256: {precomputedHash}
            </div>
          )}
        </div>
      )}

      {progress && uploading && (
        <div className="w-full bg-[#0d0f12] rounded h-2 overflow-hidden border border-[#2a2f36]">
          <div
            className="bg-red-600 h-full transition-all"
            style={{ width: `${pct}%` }}
          />
          <div className="text-xs text-gray-500 mt-1">
            {pct}% ({(progress.loaded / 1024 / 1024).toFixed(1)} /
            {' '}{(progress.total / 1024 / 1024).toFixed(1)} MB)
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-xs rounded px-2 py-1">
          {error}
        </div>
      )}

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={uploading}
            className="text-xs text-gray-400 hover:text-gray-300 px-3 py-1.5"
          >
            cancelar
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleUpload()}
          disabled={!file || hashing || uploading || disabled}
          className={btnCls}
        >
          {uploading
            ? `Uploading ${pct}%...`
            : hashing
              ? 'Computando hash...'
              : 'Upload pro R2'}
        </button>
      </div>
    </div>
  );
}
